// Groups an ocean offer's charge lines by container for the rate row's
// "Container rates" cell and the Charge Breakdown popover.

/**
 * @typedef {"Origin" | "Freight" | "Destination"} ChargeSection
 * @typedef {"Equipment" | "B/L"} ChargeBasis
 *
 * One SELL charge line (see chargeLinesFromCharges).
 * @typedef {Object} ChargeLine
 * @property {ChargeSection} section
 * @property {string} name          e.g. "Documentation Fee"
 * @property {string} equipment     e.g. "40GP"
 * @property {number} qty
 * @property {number} rate
 * @property {string} rateCurrency
 * @property {number} amount
 * @property {string} calc          e.g. "Subject To"
 * @property {ChargeBasis} basis
 *
 * A container line from the search query.
 * @typedef {Object} RequestedContainer
 * @property {string} type          e.g. "45HC"
 * @property {number} qty
 * @property {number} weightKg      line total
 *
 * @typedef {Object} SectionGroup
 * @property {ChargeSection} section
 * @property {ChargeLine[]} lines
 * @property {number} subtotal
 *
 * @typedef {Object} ContainerGroup
 * @property {string} key           normalised code, e.g. "40GP"
 * @property {string} equipment
 * @property {number|null} qty      null for equipment the search did not ask for ("General")
 * @property {number} weightKg
 * @property {boolean} requested
 * @property {SectionGroup[]} sections  non-empty, Origin → Freight → Destination
 * @property {number} total         includes B/L lines
 * @property {number|null} unitRate round(total / qty)
 *
 * @typedef {Object} UnavailableContainer
 * @property {string} key
 * @property {string} equipment
 * @property {number} qty
 * @property {number} weightKg
 *
 * @typedef {Object} ContainerBreakdown
 * @property {ContainerGroup[]} priced
 * @property {UnavailableContainer[]} unavailable
 * @property {number} requestedCount
 * @property {number} pricedRequestedCount
 * @property {number} total         sum of priced groups; display the API total instead
 */

export const SECTION_ORDER = ["Origin", "Freight", "Destination"];

const SECTION_BY_LEG = {
  ORIGIN: "Origin",
  FREIGHT: "Freight",
  DESTINATION: "Destination",
};

// "20GP" / "20 GP" / "20gp" all compare equal.
export const equipmentKey = (code) =>
  String(code || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

const toNumber = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
};

// "B/L", "BL", "per B/L" → B/L; anything else is billed per equipment.
const toBasis = (basis) => {
  const key = equipmentKey(basis);
  return key === "BL" || key === "PERBL" ? "B/L" : "Equipment";
};

/**
 * Adapts offer.productPrice.charges (as built in OceanMainRateComNew) into
 * ChargeLine[]. Only SELL lines on a known leg are kept, same as
 * computeSellBreakdown.
 * @returns {ChargeLine[]}
 */
export function chargeLinesFromCharges(charges) {
  const out = [];
  const list = charges || [];
  for (let i = 0; i < list.length; i++) {
    const c = list[i];
    if (c.rateType !== "SELL") continue;
    const section = SECTION_BY_LEG[String(c.rateTypeCode || "").toUpperCase()];
    if (!section) continue;
    out.push({
      section,
      name: c.description || "Charge",
      equipment: c.containerType || "General",
      qty: toNumber(c.qty) || 1,
      rate: toNumber(c.rate),
      rateCurrency: c.rateCurrency || "USD",
      amount: toNumber(c.amount),
      calc: String(c.calcType ?? "").trim(),
      basis: toBasis(c.basis),
    });
  }
  return out;
}

/**
 * Groups charge lines by equipment, then by section (Origin → Freight →
 * Destination). B/L lines stay in the container they are billed against.
 * A requested container with no charge line is unavailable and adds 0.
 * Totals are computed on raw numbers; round only for display.
 * @param {ChargeLine[]} lines
 * @param {RequestedContainer[]} requestedContainers
 * @returns {ContainerBreakdown}
 */
export function groupChargesByContainer(lines, requestedContainers) {
  const requested = requestedContainers || [];
  const chargeLines = lines || [];

  // Requested containers by key. The same type entered twice is merged.
  const requestedByKey = {};
  const requestedOrder = [];
  for (let i = 0; i < requested.length; i++) {
    const r = requested[i];
    const key = equipmentKey(r.type);
    if (!key) continue;
    const existing = requestedByKey[key];
    if (existing) {
      existing.qty += toNumber(r.qty) || 1;
      existing.weightKg += toNumber(r.weightKg);
      continue;
    }
    const entry = {
      key,
      equipment: String(r.type).toUpperCase(),
      qty: toNumber(r.qty) || 1,
      weightKg: toNumber(r.weightKg),
    };
    requestedByKey[key] = entry;
    requestedOrder.push(entry);
  }

  const workingByKey = {};
  const working = [];
  for (let i = 0; i < chargeLines.length; i++) {
    const line = chargeLines[i];
    const key = equipmentKey(line.equipment) || "GENERAL";
    let w = workingByKey[key];
    if (!w) {
      const req = requestedByKey[key];
      w = {
        group: {
          key,
          equipment: req ? req.equipment : line.equipment || "General",
          qty: req ? req.qty : null,
          weightKg: req ? req.weightKg : 0,
          requested: !!req,
          sections: [],
          total: 0,
          unitRate: null,
        },
        bySection: { Origin: [], Freight: [], Destination: [] },
      };
      workingByKey[key] = w;
      working.push(w);
    }
    const bucket = w.bySection[line.section];
    if (!bucket) continue; // unknown section
    bucket.push(line);
    w.group.total += toNumber(line.amount);
  }

  const priced = [];
  const pricedKeys = {};
  for (let i = 0; i < working.length; i++) {
    const w = working[i];
    const sections = [];
    for (let s = 0; s < SECTION_ORDER.length; s++) {
      const section = SECTION_ORDER[s];
      const sectionLines = w.bySection[section];
      if (sectionLines.length === 0) continue;
      let subtotal = 0;
      for (let l = 0; l < sectionLines.length; l++) subtotal += toNumber(sectionLines[l].amount);
      sections.push({ section, lines: sectionLines, subtotal });
    }
    if (sections.length === 0) continue;
    const group = w.group;
    group.sections = sections;
    group.unitRate = group.qty && group.qty > 0 ? Math.round(group.total / group.qty) : null;
    priced.push(group);
    pricedKeys[group.key] = true;
  }

  // Requested containers first, by equipment code (20GP, 40GP, 40HC, …),
  // then anything the search did not ask for.
  priced.sort((a, b) => {
    if (a.requested !== b.requested) return a.requested ? -1 : 1;
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });

  const unavailable = [];
  for (let i = 0; i < requestedOrder.length; i++) {
    if (!pricedKeys[requestedOrder[i].key]) unavailable.push(requestedOrder[i]);
  }

  let total = 0;
  for (let i = 0; i < priced.length; i++) total += priced[i].total;

  return {
    priced,
    unavailable,
    requestedCount: requestedOrder.length,
    pricedRequestedCount: requestedOrder.length - unavailable.length,
    total,
  };
}

// ── Display helpers ──

const money2 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const whole = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

export const formatMoney = (n) => money2.format(toNumber(n));
export const formatWhole = (n) => whole.format(Math.round(toNumber(n)));

// "2 × 20GP", or just the code for equipment the search did not ask for.
export const containerLabel = (c) => (c.qty ? `${c.qty} × ${c.equipment}` : c.equipment);
